import React, { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { superAdminAPI } from '../../services/api';

const AddDraftQuestionModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    questionText: '',
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ],
    subject: '',
    topic: '',
    subtopic: '',
    difficultyLevel: 'Medium',
    kitId: '',
    tenantId: '',
    tags: [],
    examTypes: [],
    explanation: '',
    questionType: 'MCQ_SINGLE_CORRECT',
    examName: '',
    mockTestName: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [examTypeInput, setExamTypeInput] = useState('');

  // Options data
  const [tenants, setTenants] = useState([]);
  const [kits, setKits] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);

  const difficultyOptions = ['Easy', 'Medium', 'Hard'];
  const questionTypes = [
    'TEXT_LATEX',
    'MCQ_SINGLE_CORRECT',
    'TRUE_FALSE',
    'FILL_IN_THE_BLANK'
  ];



  const resetForm = () => {
    setFormData({
      questionText: '',
      options: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ],
      subject: '',
      topic: '',
      subtopic: '',
      difficultyLevel: 'Medium',
      kitId: '',
      tenantId: '',
      tags: [],
      examTypes: [],
      explanation: '',
      questionType: 'MCQ_SINGLE_CORRECT',
      examName: '',
      mockTestName: ''
    });
    setTagInput('');
    setExamTypeInput('');
    setError('');
  };

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      resetForm();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch tenants, kits, and subjects in parallel
      const [tenantsResponse, kitsResponse, subjectsResponse] = await Promise.all([
        superAdminAPI.getAllTenants({ size: 100 }), // Get first 100 tenants
        superAdminAPI.getActiveKits(),
        superAdminAPI.getDraftQuestionSubjects() // Get subjects from draft questions
      ]);

      if (tenantsResponse.data?.content) {
        setTenants(tenantsResponse.data.content);
      }

      if (kitsResponse.data) {
        setKits(kitsResponse.data);
      }

      if (subjectsResponse.data) {
        setSubjectOptions(subjectsResponse.data);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load form data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = {
      ...newOptions[index],
      [field]: value
    };

    // If marking as correct, unmark others for single correct
    if (field === 'isCorrect' && value && formData.questionType === 'MCQ_SINGLE_CORRECT') {
      newOptions.forEach((option, i) => {
        if (i !== index) {
          option.isCorrect = false;
        }
      });
    }

    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, { text: '', isCorrect: false }]
      }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addExamType = () => {
    if (examTypeInput.trim() && !formData.examTypes.includes(examTypeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        examTypes: [...prev.examTypes, examTypeInput.trim()]
      }));
      setExamTypeInput('');
    }
  };

  const removeExamType = (examTypeToRemove) => {
    setFormData(prev => ({
      ...prev,
      examTypes: prev.examTypes.filter(type => type !== examTypeToRemove)
    }));
  };

  const validateForm = () => {
    if (!formData.questionText.trim()) {
      setError('Question text is required');
      return false;
    }

    if (!formData.subject.trim()) {
      setError('Subject is required');
      return false;
    }

    if (formData.options.length < 2) {
      setError('At least 2 options are required');
      return false;
    }

    if (!formData.options.some(option => option.text.trim())) {
      setError('At least one option must have text');
      return false;
    }

    if (!formData.options.some(option => option.isCorrect)) {
      setError('At least one option must be marked as correct');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare the data for submission
      const submissionData = {
        ...formData,
        // Set required fields that might be missing
        type: 'MCQ_SINGLE_CORRECT', // Default question type
        correctAnswer: generateCorrectAnswer(), // Generate from selected options
        difficultyLevel: formData.difficultyLevel.toUpperCase(), // Ensure uppercase
        // Filter out empty options
        options: formData.options.filter(option => option.text.trim() !== '')
      };

      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      console.error('Error creating draft question:', error);
      setError(error.response?.data?.message || 'Failed to create draft question');
    } finally {
      setLoading(false);
    }
  };

  // Generate correct answer string from selected options
  const generateCorrectAnswer = () => {
    const correctIndices = formData.options
      .map((option, index) => option.isCorrect ? (index + 1).toString() : null)
      .filter(index => index !== null);

    return correctIndices.join(',') || '1'; // Default to '1' if none selected
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Draft Question</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question Text *
            </label>
            <textarea
              value={formData.questionText}
              onChange={(e) => handleInputChange('questionText', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="Enter the question text..."
              required
            />
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Mathematics"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => handleInputChange('topic', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Algebra"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty Level
              </label>
              <select
                value={formData.difficultyLevel}
                onChange={(e) => handleInputChange('difficultyLevel', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {difficultyOptions.map(difficulty => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Answer Options *
            </label>
            <div className="space-y-3">
              {formData.options.map((option, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={option.isCorrect}
                    onChange={(e) => handleOptionChange(index, 'isCorrect', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Option ${index + 1}`}
                  />
                  {formData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}

              {formData.options.length < 6 && (
                <button
                  type="button"
                  onClick={addOption}
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Option</span>
                </button>
              )}
            </div>
          </div>

          {/* Explanation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Explanation
            </label>
            <textarea
              value={formData.explanation}
              onChange={(e) => handleInputChange('explanation', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Explain the correct answer..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Draft Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDraftQuestionModal;
